import { Send } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import type { AssetComment, Member } from "../../lib/api.ts";
import { formatOffset, parseOffset } from "../../lib/timeline.ts";
import { Avatar, ICON } from "../../ui/index.ts";

/**
 * What everyone said about one recording, and the box to say the next thing in.
 *
 * Two parts, because they sit in two places. The thread is the body of the sheet under the
 * picture — a conversation is read, and a list that is read wants room. The box is pinned to the
 * foot, where it stays as the thread scrolls past it, which is where everybody has learned to
 * look for the place to type.
 *
 * On a video a comment can carry a position, because "the bit at 1:24" is a different remark
 * from one about the whole clip. The field is filled in from wherever the clip was left when it
 * was last watched, so the common case is a glance rather than arithmetic. On a photo there is
 * no such thing, so it is not offered.
 */
export function MediaComments({
  comments,
  members,
}: {
  comments: AssetComment[];
  members: Member[];
}) {
  const name = (id: string) => members.find((m) => m.userId === id)?.displayName ?? "A traveller";
  const colorOf = (id: string) => members.find((m) => m.userId === id)?.color ?? null;

  // Nothing said yet is not news: the box underneath is already the invitation, and a line
  // announcing the silence only makes the sheet taller.
  if (comments.length === 0) return null;
  return (
    <ul className="plain media-comment-list" aria-label="Comments">
      {comments.map((c) => (
        <li key={c.id} className={c.parentId ? "reply" : undefined}>
          <Avatar name={name(c.authorId)} color={colorOf(c.authorId)} size="sm" />
          <span className="media-comment-body">
            <span className="media-comment-who">
              {name(c.authorId)}
              {c.offsetMs !== null && <span className="pill">at {formatOffset(c.offsetMs)}</span>}
            </span>
            <span>{c.body}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The box the next thing is said in, pinned to the foot of the sheet. */
export function CommentForm({
  id,
  isVideo,
  mark,
  onAdd,
}: {
  /** This card's own field, so the remark count on the line above focuses the right one. */
  id: string;
  isVideo: boolean;
  /** Where the clip was left when it was last watched, in ms; null if it has not been. */
  mark: number | null;
  onAdd: (c: { body: string; offsetMs?: number }) => Promise<void>;
}) {
  const [text, setText] = useState("");
  const [at, setAt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Watching a clip is how you find the moment worth saying something about, so closing it fills
  // the field in. It is still a plain text field: whatever is typed over it wins.
  useEffect(() => {
    if (mark !== null) setAt(formatOffset(mark));
  }, [mark]);

  function submit(e: FormEvent) {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    const offset = at.trim() === "" ? undefined : parseOffset(at);
    if (offset === null) {
      setError("Use a time like 1:24, or leave it empty for the whole recording.");
      return;
    }
    setBusy(true);
    setError(null);
    onAdd({ body, ...(offset === undefined ? {} : { offsetMs: offset }) })
      .then(() => {
        setText("");
        setAt("");
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setBusy(false));
  }

  return (
    <div className="media-composer">
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <form className="media-composer-row" onSubmit={submit}>
        {isVideo && (
          <input
            className="composer-at"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            placeholder="0:00"
            aria-label="Comment time"
          />
        )}
        <input
          id={id}
          className="composer-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Say something"
          maxLength={8000}
          aria-label="Comment"
        />
        {/* Disabled until there is something to send, so the control never promises an action
            that would do nothing. */}
        <button className="composer-send" disabled={busy || text.trim() === ""}>
          <Send {...ICON} aria-hidden="true" />
          <span className="sr">Post comment</span>
        </button>
      </form>
    </div>
  );
}
